import { useEffect, useRef } from 'react';

import { SocialCurrency, useSocialCurrencyStore } from '@/hooks/use-social-currency';
import { RootStackParamList } from '@/navigation/types';
import { useQuery } from '@tanstack/react-query';

import { getCasinoGamesByIds } from './use-casino-game-history';
import { isCasinoGameAvailableOnPlatform } from './use-casino-games';
import { getGeoToken, useCasinoSession, useRenewCasinoSession } from './use-casino-session';
import { useFormattedCasinoGameUrl } from './use-formatted-casino-game-url';
import { useSelectedCountryCode } from './use-selected-country-code';

type CasinoGameWebViewParams = RootStackParamList['CasinoGameWebView'];

/**
 * Resolves the casino game launch URL and surrounding loading/error state for
 * CasinoGameWebViewScreen. Uses the caller-supplied `gameParams` when present;
 * otherwise the game is fetched fresh by `gameId`.
 */
export const useCasinoGameWebView = ({ gameId, gameParams }: CasinoGameWebViewParams) => {
    const isCoinsCurrency = useSocialCurrencyStore(state => state.activeCurrency === SocialCurrency.COINS);
    const selectedCountryCode = useSelectedCountryCode();
    const shouldFetchGame = !gameParams;

    const {
        data: gameByIdData,
        isLoading: isGameByIdLoading,
        isError: isGameByIdError,
    } = useQuery({
        queryKey: ['casino-game-by-id', gameId, selectedCountryCode],
        queryFn: () => getCasinoGamesByIds([gameId], selectedCountryCode),
        enabled: shouldFetchGame,
    });
    const fetchedGame = gameByIdData?.items.find(isCasinoGameAvailableOnPlatform);
    const launchUrl = shouldFetchGame ? fetchedGame?.launchUrl : gameParams.launchUrl;
    const title = shouldFetchGame ? fetchedGame?.name : gameParams.title;

    const { data: geoPayload, isFetching: isGeoTokenFetching } = useQuery({
        queryKey: ['casino-geotoken'],
        queryFn: getGeoToken,
        enabled: !isCoinsCurrency,
    });
    const { isLoading: isSessionLoading, data: sessionData } = useCasinoSession();
    const { mutate: renewSession } = useRenewCasinoSession();
    const formattedUrl = useFormattedCasinoGameUrl(launchUrl);
    const sessionId = sessionData?.data.session_id;
    const geoToken = geoPayload?.geo_session_token;

    const isLoading = isSessionLoading || (shouldFetchGame && isGameByIdLoading);
    const gameLoadFailed = shouldFetchGame && !isGameByIdLoading && (isGameByIdError || !fetchedGame);
    const sessionLoadFailed = !isSessionLoading && !sessionId;
    const launchUrlMissing = !isLoading && !launchUrl;
    const failsToLoad = gameLoadFailed || sessionLoadFailed || launchUrlMissing;

    // Renew session when geoToken becomes stale and we have an active session
    const prevGeoTokenRef = useRef<string | undefined>(undefined);
    useEffect(() => {
        if (isCoinsCurrency) {
            return;
        }
        if (!isGeoTokenFetching && sessionId && geoToken) {
            if (prevGeoTokenRef.current !== geoToken) {
                renewSession({ sessionId, geoPayload });
            }
        }
        prevGeoTokenRef.current = geoToken;
    }, [isCoinsCurrency, isGeoTokenFetching, sessionId, geoToken, geoPayload, renewSession]);

    return {
        title,
        formattedUrl,
        isLoading,
        failsToLoad,
    };
};
